// PriceCalculator — Renders the in-flow "Your price" card and the
// matching floating overlay that follows the customer until they scroll
// past the in-flow card.  All pricing math happens server-side; this
// component only displays the result and tracks visibility.
import { forwardRef, useEffect, useRef, useState } from "react";
import { Clock } from "lucide-react";

import { money, moneyUnit } from "../lib/api";

function PriceCard({ price, calcing, quantity, dataTestId }) {
  // Production lead time comes from the admin-configured product field
  // (turnaround_options or the product's production_lead_time fallback).
  // If neither is set, we hide the row entirely so the card doesn't fake
  // a value the storefront can't actually commit to.
  const eta = (price?.eta_days || "").trim();
  const dieCut = price?.die_cut;
  const isFallback = price?.fallback === true;
  return (
    <div className="bg-[#002A4F] text-white rounded-sm p-4 shadow-2xl ring-1 ring-white/10" data-testid={dataTestId}>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-[0.18em] text-slate-300 font-bold">
          {isFallback ? "Starting at" : "Your price"}
        </span>
        {calcing && <span className="text-[10px] text-slate-400">calculating…</span>}
      </div>
      <div className="font-display font-black text-3xl text-[#DD6B33] leading-none mb-1" data-testid="total-price">
        {price ? money(price.line_total) : "—"}
      </div>
      <div className="text-xs text-slate-300">
        {price ? (
          isFallback ? (
            <>from <span className="font-bold text-white">{moneyUnit(price.unit_price)}</span> / unit · finish configuring to lock final price</>
          ) : (
            <>at <span className="font-bold text-white">{moneyUnit(price.unit_price)}</span> / unit · {quantity.toLocaleString()} units</>
          )
        ) : (
          "—"
        )}
      </div>
      {dieCut?.applied && (
        <div className="mt-2.5 pt-2.5 border-t border-white/10 flex items-start gap-2 text-[11px]" data-testid="die-cut-breakdown">
          <span className="text-[#DD6B33] font-bold uppercase tracking-wider">Die-cut</span>
          <span className="text-slate-300">
            includes <span className="text-white font-bold">+{money(dieCut.surcharge)}</span> custom-die tooling fee
          </span>
        </div>
      )}
      {eta && (
        <div className="mt-2.5 pt-2.5 border-t border-white/10 flex items-center gap-2 text-[11px] text-slate-300">
          <Clock className="w-3.5 h-3.5 text-[#DD6B33] flex-shrink-0" />
          Production: <span className="text-white font-semibold">{eta}</span>
        </div>
      )}
    </div>
  );
}

/**
 * <FloatingPriceCard/> — pinned to the top-right; fades out once the user
 * scrolls so the in-flow `<InFlowPriceCard/>` enters the viewport.
 */
export function FloatingPriceCard({ price, calcing, quantity, isInView, visible }) {
  if (!visible || !price) return null;
  return (
    <div
      className={`hidden lg:block fixed top-32 right-6 z-40 w-[320px] transition-all duration-300 ease-out ${
        isInView ? "opacity-0 -translate-y-2 pointer-events-none" : "opacity-100 translate-y-0"
      }`}
      aria-hidden={isInView}
      data-testid="floating-price"
    >
      <PriceCard price={price} calcing={calcing} quantity={quantity} dataTestId="floating-price-card" />
    </div>
  );
}

/**
 * <InFlowPriceCard/> — the canonical in-document price card.  Forwards a
 * ref so the parent can observe its visibility (used by the floating card).
 */
export const InFlowPriceCard = forwardRef(function InFlowPriceCard(
  { price, calcing, quantity },
  ref,
) {
  return (
    <div ref={ref} className="mt-6" data-testid="price-summary">
      <PriceCard price={price} calcing={calcing} quantity={quantity} dataTestId="price-summary-card" />
    </div>
  );
});

/**
 * Shared hook: returns `[ref, isInView]` — set `ref` on any element you
 * want to track and `isInView` will flip to true when ≥35% of it scrolls
 * onto the screen (matching the previous threshold).
 */
export function useInViewport(deps = []) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.35, rootMargin: "-130px 0px 0px 0px" },
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return [ref, inView];
}
